import { geometry } from "@turf/turf";
import { LocationData } from "./models";

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0
});

export const getCounts = (locations?: LocationData[]): string => {
  if (!locations || locations.length === 0) return "0/0";
  return `${
    locations?.filter((location: LocationData) => location.checked).length
  }/${locations?.length}`;
};

export const getCheckedCounts = (locations?: LocationData[]): string => {
  if (!locations || locations.length === 0) return "0";
  return `${
    locations?.filter((location: LocationData) => location.checked).length
  }`;
};

/**
 * 根据传入条件过滤地点
 * @param data - 需要过滤的地点
 * @param unit - 可视化值
 * @param multipleLocationsSearch - 地点name过滤
 * @param locationsFilterInfo - 地点的其他filter过滤
 * @param filterChecked - 是否过滤未选中地点
 * @returns 过滤后的地点数据
 */
export const handleFilterLocations = ({
  data,
  unit,
  multipleLocationsSearch,
  locationsFilterInfo,
  filterChecked
}: {
  data: any[];
  unit: string;
  multipleLocationsSearch: string;
  locationsFilterInfo: any;
  filterChecked?: boolean;
}) => {
  let arr = [...data];

  if (
    locationsFilterInfo[0].value &&
    locationsFilterInfo[0].value !== "allLocations"
  ) {
    if (unit === "numberOfLocations") {
      arr.sort((a: any, b: any) => {
        if (!a || !b) {
          return 0;
        }
        const aValue = a?.code;
        const bValue = b?.code;
        if (!aValue) {
          return -1;
        } else if (!bValue) {
          return 1;
        }
        return String(aValue).localeCompare(String(bValue), "zh-CN", {
          numeric: true
        });
      });
    } else {
      arr.sort((a: any, b: any) => {
        if (!a || !b) {
          return 0;
        }

        const aValue = a?.[unit];
        const bValue = b?.[unit];

        if (!aValue) {
          return 1;
        } else if (!bValue) {
          return -1;
        }
        return String(bValue).localeCompare(String(aValue), "zh-CN", {
          numeric: true
        });
      });
    }
  }

  const filteredData = arr.filter((item: any) => {
    if (filterChecked && !item.checked) {
      return false;
    }
    if (
      multipleLocationsSearch &&
      !item.name.toLowerCase().includes(multipleLocationsSearch.toLowerCase())
    ) {
      return false;
    }
    if (unit && unit !== "numberOfLocations" && !item?.[unit]) {
      return false;
    }

    return locationsFilterInfo.every((filter: any) => {
      if (filter.children) {
        return filter.children.every((child: any) => {
          if (child.type === "number") {
            const value = item[child.title];
            // 如果 min 或 max 有一个有值，就进行过滤
            if (child.min !== undefined || child.max !== undefined) {
              return (
                (!child.min || value >= child.min) &&
                (!child.max || value <= child.max)
              );
            }
            return true; // 如果 min 和 max 都没有值，则不做过滤
          } else if (child.type === "time") {
            const itemDate = new Date(item[child.title]).getTime(); // 将后端返回的日期字符串转为时间戳
            // 如果 min 或 max 有一个有值，就进行过滤
            if (child.min || child.max) {
              return (
                itemDate &&
                (!child.min || itemDate >= child.min) &&
                (!child.max || itemDate <= child.max)
              );
            }
            return true; // 如果 min 和 max 都没有值，则不做过滤
          } else if (child.type === "select") {
            // 如果 child.value 是空数组，则不做过滤，直接返回 true
            return (
              !child.value.length || child.value.includes(item[child.title])
            );
          }
          return true;
        });
      }
      return true;
    });
  });

  // 获取分页大小
  const pageSize = locationsFilterInfo[0]?.value;

  // 如果 pageSize 是 "allLocations"，则不分页，直接返回所有数据
  if (pageSize === "allLocations" || !pageSize) {
    return filteredData;
  }

  // 转换 pageSize 为数字
  const pageLimit = parseInt(pageSize, 10);

  // 分页处理
  const paginatedData = filteredData.slice(0, pageLimit);

  return paginatedData;
};

export const formatCurrency = (num: number): string =>
  formatter.format(num).replace("$", "");

export const statusBgColor = (status: string) => {
  switch (status) {
    case "1":
      return "#52C41A";
    case "2":
      return "#FF4D4F";
    case "3":
      return "#FAAD14";
    case "4":
      return "#999999";
    default:
      return "#999999";
  }
};

export const statusText = (status: string) => {
  switch (status) {
    case "1":
      return "active";
    case "2":
      return "overdue";
    case "3":
      return "closed";
    case "4":
      return "notActivated";
    default:
      return status;
  }
};

export const statusText1 = (status: string) => {
  switch (status) {
    case "1":
      return "success";
    default:
      return "failed";
  }
};

export function formatAmount(
  value: number | string,
  locale: string = "zh-CN"
): string {
  const num = typeof value === "string" ? Number(value) : value;

  if (!Number.isFinite(num)) return "";

  //  四舍五入取整
  const rounded = Math.round(num);

  return new Intl.NumberFormat(locale, {
    style: "decimal", // 不使用 currency
    maximumFractionDigits: 0, // 去掉小数
    minimumFractionDigits: 0
  }).format(rounded);
}

/**
 * 格式化数字为 K/M/B/T 缩写。
 * @param num - 输入数字或数字字符串
 * @param is - true: 只保留整数（带智能进位）; false: 原逻辑（保留1位小数，去掉末尾.0）
 * @returns 格式化字符串；若输入非法返回空字符串
 */
export function formatKM(num: number | string, is: boolean = false): string {
  const n = typeof num === "string" ? Number(num) : num;
  if (!Number.isFinite(n)) return "";

  const sign = n < 0 ? "-" : "";
  let abs = Math.abs(n);

  // 单位表（可扩展）
  const units = [
    { suffix: "", value: 1 },
    { suffix: "K", value: 1e3 },
    { suffix: "M", value: 1e6 },
    { suffix: "B", value: 1e9 },
    { suffix: "T", value: 1e12 }
  ] as const;

  // 找到“基础单位索引”：尽量用大的单位
  let i = units.length - 1;
  while (i > 0 && abs < units[i].value) i--;

  if (is) {
    // === 模式1：只保留整数（智能进位）===
    // 例如 999,500 -> 1M；1,250 -> 1K
    let scaled = abs / units[i].value;
    let rounded = Math.round(scaled);

    // 跨单位进位：999.5K → 1000K → 1M
    while (rounded >= 1000 && i < units.length - 1) {
      i++;
      rounded = Math.round(rounded / 1000);
    }

    if (i === 0) return sign + String(Math.round(abs));
    return sign + String(rounded) + units[i].suffix;
  } else {
    // === 模式2：原逻辑（保留一位小数，去掉 .0）===
    if (abs >= 1e12)
      return sign + (abs / 1e12).toFixed(1).replace(/\.0$/, "") + "T";
    if (abs >= 1e9)
      return sign + (abs / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
    if (abs >= 1e6)
      return sign + (abs / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    if (abs >= 1e3)
      return sign + (abs / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
    return sign + String(abs); // < 1000 原样（你的旧实现如此）
  }
}

export function pctRaw(n: number, d: number): number {
  if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return 0;

  // 百分比的绝对值
  const sign = n * d < 0 ? -1 : 1;
  const p = Math.abs((n / d) * 100);

  if (p === 0) return 0;

  if (p >= 1) {
    // 直接向下取整到整数百分比
    return sign * Math.floor(p);
  }

  // p < 1：保留第一位有效数字（向下取）
  // 示例：p = 0.003106
  const k = Math.floor(Math.log10(p)); // -3
  const pow = Math.pow(10, k); // 10^-3 = 0.001
  const firstDigit = Math.floor(p / pow); // floor(3.106) = 3
  const value = firstDigit * pow; // 3 * 0.001 = 0.003

  // 需要的小数位数（用于消除浮点误差）：-k
  const decimals = -k;
  return sign * Number(value.toFixed(decimals));
}

export function safePctRaw(num: number, den: number): number {
  return Number(num.toFixed()) === 1 ? 0 : pctRaw(num, den);
}

export function getMarkerUrl(fillColor = "#fff8a1") {
  return `/icon_map_pin_${fillColor.split("#").join("")}.svg`;
}

export const styleArr = [
  { bg: "#fff8a1", size: "50px" },
  { bg: "#ffcb8a", size: "55px" },
  { bg: "#ff9c86", size: "60px" },
  { bg: "#ee6c91", size: "60px" },
  { bg: "#cb419c", size: "65px" },
  { bg: "#9c2499", size: "85px" },
  { bg: "#61168e", size: "85px" },
  { bg: "#001789", size: "85px" }
];

////获取当前值对应的图例色块
export function getStyleIndex(
  value: number | string,
  total: number,
  min = 1 // 新增的最小值参数，默认沿用原来从 1 开始
) {
  const segments = styleArr.length;
  const base = Math.pow(total / min, 1 / segments); // 按最小值到 total 等比划分
  let current = min;
  let index = 0;

  for (let i = 1; i <= segments; i++) {
    const next = current * base;

    const isStart = Number(value) >= Number(current);
    const isEnd =
      i === segments
        ? Number(value) <= Number(Math.ceil(next)) // 最后一段闭区间
        : Number(value) < Number(next); // 其他段半开区间

    if (isStart && isEnd) {
      index = i - 1;
      break;
    }
    current = next;
  }
  return index;
}

//获取地图当前视角内的地点
export function getLocalScaleLocations(data: any, map: any) {
  const bounds = map.getBounds();
  if (!bounds) return data;
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  return data.filter((it: any) => {
    if (!it.checked) return false;
    const lng = it.longitude;
    const lat = it.latitude;
    // 若跨经度 180°（极少见），给出容错
    const inLng =
      ((sw?.lng ?? -180) <= lng && lng <= (ne?.lng ?? 180)) ||
      ((sw?.lng ?? 0) > (ne?.lng ?? 0) &&
        (lng >= (sw?.lng ?? 0) || lng <= (ne?.lng ?? 0)));
    const inLat = (sw?.lat ?? -90) <= lat && lat <= (ne?.lat ?? 90);
    return inLng && inLat;
  });
}

export const defaultColumnList = [
  {
    title: "general",
    expanded: true,
    children: [
      { title: "code", show: true, width: 100 },
      { title: "name", show: true }
    ]
  },
  {
    title: "address",
    expanded: false,
    children: [
      { title: "zipCode", show: false },
      { title: "streetNumber", show: false },
      { title: "place", show: false },
      { title: "county", show: false },
      { title: "province", show: false },
      { title: "country", show: false }
    ]
  },
  {
    title: "insurance",
    expanded: false,
    children: [
      { title: "insuredName", show: false },
      { title: "insuredCode", show: false },
      { title: "inceptionDate", show: false },
      { title: "expirationDate", show: false },
      { title: "evaluationDate", show: false },
      { title: "currency", show: false },
      { title: "perilsCovered", show: false },
      { title: "occupancy", show: false },
      { title: "numberOfRisks", type: "number", show: false },
      { title: "building", type: "number", show: false },
      { title: "content", type: "number", show: false },
      { title: "businessInterruption", type: "number", show: false },
      { title: "propertyDamage", type: "number", show: false },
      { title: "limit", type: "number", show: false },
      { title: "deductible", type: "number", show: false },
      { title: "other", type: "number", show: false },
      { title: "total", type: "number", show: false }
    ]
  }
];

export const defaultFilterList = [
  {
    title: "topLocations",
    options: [],
    value: ""
  },
  {
    title: "insurance",
    expanded: false,
    showMore: "1", //1收起 2展开
    children: [
      {
        title: "occupancy",
        type: "select",
        isTop: true,
        options: [],
        value: []
      },
      {
        title: "perilsCovered",
        type: "select",
        isTop: true,
        options: [],
        value: []
      },
      {
        title: "insuredName",
        type: "select",
        options: [],
        value: []
      },
      {
        title: "insuredCode",
        type: "select",
        options: [],
        value: []
      },
      {
        title: "inceptionDate",
        type: "time",
        min: null,
        max: null
      },
      {
        title: "expirationDate",
        type: "time",
        min: null,
        max: null
      },
      {
        title: "evaluationDate",
        type: "time",
        min: null,
        max: null
      },
      {
        title: "total",
        type: "number",
        min: "",
        max: ""
      },
      {
        title: "numberOfRisks",
        type: "number",
        min: "",
        max: ""
      },
      {
        title: "other",
        type: "number",
        min: "",
        max: ""
      },
      {
        title: "limit",
        type: "number",
        min: "",
        max: ""
      },
      {
        title: "building",
        type: "number",
        min: "",
        max: ""
      },
      {
        title: "content",
        type: "number",
        min: "",
        max: ""
      },
      {
        title: "businessInterruption",
        type: "number",
        min: "",
        max: ""
      },
      {
        title: "propertyDamage",
        type: "number",
        min: "",
        max: ""
      },
      {
        title: "deductible",
        type: "number",
        min: "",
        max: ""
      }
      // {
      //   title: "currency",
      //   type: "select",
      //   options: [],
      //   value: []
      // }
    ]
  },
  {
    title: "geoLocation",
    expanded: false,
    children: [
      {
        title: "province",
        type: "select",
        options: [],
        value: []
      },
      {
        title: "country",
        type: "select",
        options: [],
        value: []
      },
      {
        title: "county",
        type: "select",
        options: [],
        value: []
      }
    ]
  }
];

export function createDefaultColumnList() {
  return JSON.parse(JSON.stringify(defaultColumnList));
}

export function createDefaultFilterList() {
  return JSON.parse(JSON.stringify(defaultFilterList));
}

export function createDefaultShapList() {
  return JSON.parse(JSON.stringify(defaultShapeList));
}

export function toFixedTwo(num: number) {
  return num % 1 === 0 ? num : num.toFixed(2);
}

export const defaultShapeList = [
  // {
  //   id: "Shape A",
  //   category: "polygon",
  //   name: "Shape A",
  //   type: "",
  //   geometry: {
  //     geometry: {
  //       coordinates: [
  //         [
  //           [72.9802382571318, 59.31350808226965],
  //           [72.51148222605724, 59.303610346527975],
  //           [71.11234604274645, 59.234467293203565],
  //           [72.9802382571318, 59.31350808226965]
  //         ]
  //       ],
  //       type: "Polygon"
  //     },
  //     properties: {
  //       sourceId: "shape-g9K684K4qAwH_kr6QJ1nr",
  //       featureId: "shape-feature-CYbPMivs3Y2vUr1Uy-xdP",
  //       toolType: "shapes",
  //       areaM: 5140767366204,
  //       areaKm: 5140767.366,
  //       areaFt: 55334759404457,
  //       areaMi: 1984336.203
  //     },
  //     type: "Feature"
  //   }
  // },
  // {
  //   id: "Shape B",
  //   category: "polyline",
  //   name: "Shape B",
  //   type: "Ground",
  //   geometry: {
  //     geometry: {
  //       coordinates: [
  //         [83.10241580153632, 41.428692594717546],
  //         [113.68835330153684, 40.63309913985128]
  //       ],
  //       type: "LineString"
  //     },
  //     properties: {
  //       sourceId: "shape-gEZy1ZJfgWWw2AIlwCXLL",
  //       featureId: "shape-feature-d6MDhZYhuESgjpsCrGdRB",
  //       toolType: "shapes",
  //       distanceKm: 2553.711,
  //       distanceM: 2553711,
  //       distanceMi: 1586.802,
  //       distanceFt: 8378317
  //     },
  //     type: "Feature"
  //   }
  // }
];
